import SolanaIcon from "assets/icons/chains/solana.png";
import EthereumIcon from "assets/icons/chains/ethereum.png";
import BaseIcon from "assets/icons/chains/base.png";
import BeraIcon from "assets/icons/chains/bera.png";
import BinanceIcon from "assets/icons/chains/bsc.png";

export type ChainConfig = {
  name: "solana" | "ethereum" | "base" | "berachain" | "bsc";
  symbol: "TON" | "SOL" | "ETH" | "BASE" | "BERA" | "BSC";
  currency: "TON" | "SOL" | "ETH" | "BERA" | "BNB";
  wrappedCurrency?: "WSOL" | "WETH" | "WBNB" | "WBERA";
  walletName: "tonAddress" | "solAddress" | "ethAddress";
  nativePriceName: "tonPrice" | "solPrice" | "ethPrice" | "beraPrice" | "bnbPrice";
  nativeTokenAddress: string;
  wrappedTokenAddress?: string;
  isEvm: boolean;
  hasPriorityFee: boolean;
  hasValidatorTip: boolean;
  imageUrl: string;
  blockExplorer: string;
};

export type Chain = ChainConfig["name"];
export type ChainSymbol = ChainConfig["symbol"];
export type ChainCurrency = ChainConfig["currency"];
export type WrappedCurrency = ChainConfig["wrappedCurrency"];
export type ChainWalletName = ChainConfig["walletName"];
export type ChainNativePriceName = ChainConfig["nativePriceName"];

const CODEX_NETWORK_ID = {
  1: "ethereum",
  8453: "base",
  1399811149: "solana",
  80094: "berachain",
  56: "bsc",
  137: "polygon",
  10: "op",
  42161: "arbitrum",
  43114: "avalanche",
  250: "fantom",
  49705: "aptos",
  101: "sui",
  59144: "linea",
  2741: "abstract",
  146: "sonic",
  999: "hyperliquid",
  728126428: "tron",
  ethereum: 1,
  base: 8453,
  solana: 1399811149,
  berachain: 80094,
  bsc: 56,
  polygon: 137,
  op: 10,
  arbitrum: 42161,
  avalanche: 43114,
  fantom: 250,
  aptos: 49705,
  sui: 101,
  linea: 59144,
  abstract: 2741,
  sonic: 146,
  hyperliquid: 999,
  tron: 728126428,
};

const CHAIN_CONFIG: Record<Chain, ChainConfig> = {
  // ton: {
  //   name: "ton",
  //   symbol: "TON",
  //   currency: "TON",
  //   walletName: "tonAddress",
  //   nativePriceName: "tonPrice",
  //   nativeTokenAddress: "EQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAM9c",
  //   isEvm: false,
  //   hasPriorityFee: false,
  //   hasValidatorTip: false,
  //   imageUrl: TonIcon,
  //   blockExplorer: `https://tonviewer.com`,
  // },
  solana: {
    name: "solana",
    symbol: "SOL",
    currency: "SOL",
    walletName: "solAddress",
    nativePriceName: "solPrice",
    nativeTokenAddress: "So11111111111111111111111111111111111111112",
    wrappedCurrency: "WSOL",
    wrappedTokenAddress: "So11111111111111111111111111111111111111112",
    isEvm: false,
    hasPriorityFee: true,
    hasValidatorTip: true,
    imageUrl: SolanaIcon,
    blockExplorer: `https://solscan.io/tx`,
  },
  ethereum: {
    name: "ethereum",
    symbol: "ETH",
    currency: "ETH",
    walletName: "ethAddress",
    nativePriceName: "ethPrice",
    nativeTokenAddress: "0xEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEE",
    wrappedCurrency: "WETH",
    wrappedTokenAddress: "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2",
    isEvm: true,
    hasPriorityFee: false,
    hasValidatorTip: false,
    imageUrl: EthereumIcon,
    blockExplorer: `https://etherscan.io/tx`,
  },
  base: {
    name: "base",
    symbol: "BASE",
    currency: "ETH",
    walletName: "ethAddress",
    nativePriceName: "ethPrice",
    nativeTokenAddress: "0x4200000000000000000000000000000000000006",
    wrappedCurrency: "WETH",
    wrappedTokenAddress: "0x4200000000000000000000000000000000000006",
    isEvm: true,
    hasPriorityFee: false,
    hasValidatorTip: false,
    imageUrl: BaseIcon,
    blockExplorer: `https://basescan.org/tx`,
  },
  bsc: {
    name: "bsc",
    symbol: "BSC",
    currency: "BNB",
    walletName: "ethAddress",
    nativePriceName: "bnbPrice",
    nativeTokenAddress: "0xEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEE",
    wrappedCurrency: "WBNB",
    wrappedTokenAddress: "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c",
    isEvm: true,
    hasPriorityFee: false,
    hasValidatorTip: false,
    imageUrl: BinanceIcon,
    blockExplorer: `https://bscscan.com/tx`,
  },
  berachain: {
    name: "berachain",
    symbol: "BERA",
    currency: "BERA",
    walletName: "ethAddress",
    nativePriceName: "beraPrice",
    nativeTokenAddress: "0xEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEEE",
    wrappedCurrency: "WBERA",
    wrappedTokenAddress: "0x6969696969696969696969696969696969696969",
    isEvm: true,
    hasPriorityFee: false,
    hasValidatorTip: false,
    imageUrl: BeraIcon,
    blockExplorer: `https://berascan.com/tx`,
  },
};

const CHAINS: Record<Chain, Chain> = Object.entries(CHAIN_CONFIG).reduce((acc, [key]) => {
  acc[key as Chain] = key as Chain;
  return acc;
}, {} as Record<Chain, Chain>);

const CHAIN_CHECK_LIST: Chain[] = Object.values(CHAINS);
const NATIVE_CURRENCY_CHECK_LIST: ChainCurrency[] = [
  ...Object.values(CHAIN_CONFIG).reduce((acc, chain) => {
    acc.add(chain.currency);
    return acc;
  }, new Set() as Set<ChainCurrency>),
];
const WRAPPED_CURRENCY_CHECK_LIST: WrappedCurrency[] = [
  ...Object.values(CHAIN_CONFIG).reduce((acc, chain) => {
    if (chain.wrappedCurrency) {
      acc.add(chain.wrappedCurrency);
    }
    return acc;
  }, new Set() as Set<WrappedCurrency>),
];

const EVM_CHAINS = Object.values(CHAIN_CONFIG).reduce((acc, chain) => {
  if (chain.isEvm) {
    acc.push(chain.name);
  }
  return acc;
}, [] as Chain[]);

const CHAIN_WALLETS_MAP = Object.values(CHAIN_CONFIG).reduce((acc, chain) => {
  acc[chain.name] = chain.walletName;
  return acc;
}, {} as Record<Chain, string>);

const NATIVE_TOKEN_ADDRESS_LIST = Object.values(CHAIN_CONFIG).reduce((acc, chain) => {
  if (chain.nativeTokenAddress) {
    acc.push(chain.nativeTokenAddress);
  }
  return acc;
}, [] as string[]);

const WRAPPED_TOKEN_ADDRESS_LIST = Object.values(CHAIN_CONFIG).reduce((acc, chain) => {
  if (chain.wrappedTokenAddress) {
    acc.push(chain.wrappedTokenAddress);
  }
  return acc;
}, [] as string[]);

export {
  CHAIN_CONFIG,
  CHAINS,
  CHAIN_CHECK_LIST,
  NATIVE_CURRENCY_CHECK_LIST,
  WRAPPED_CURRENCY_CHECK_LIST,
  NATIVE_TOKEN_ADDRESS_LIST,
  WRAPPED_TOKEN_ADDRESS_LIST,
  EVM_CHAINS,
  CHAIN_WALLETS_MAP,
  CODEX_NETWORK_ID,
};
